import React, { useEffect, useRef, useState } from 'react'
import { Stack, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { v4 as uuid } from 'uuid'

import { FeedbackFormView } from './FeedbackFormView'
import { useFeedbackForm } from './useFeedbackForm'

import { ContinueToNextWitnessButton } from '@/components/Event/CrForm/components/ContinueToNextWitnessButton'
import { WitnessSwitcher } from '@/components/Event/CrForm/components/WitnessSwitcher'
import { getInitialStepsState } from '@/components/Event/CrForm/CrFormStep'
import { AttendanceStep } from '@/components/Event/CrForm/steps/AttendanceStep'
import { ClosingStep } from '@/components/Event/CrForm/steps/ClosingStep'
import { OpeningStep } from '@/components/Event/CrForm/steps/OpeningStep'
import { PreparationStep } from '@/components/Event/CrForm/steps/PreparationStep'
import { QuestioningStep } from '@/components/Event/CrForm/steps/QuestioningStep'
import { useCrParticipantsMapping } from '@/components/Event/CrForm/useCrParticipantsMapping'
import { openInvalidStepsAndScroll } from '@/components/Event/CrForm/useStepField'
import {
  appendWitnessFeedback,
  canAddWitnessFeedback,
  canRemoveWitnessFeedback,
  removeWitnessFeedback,
} from '@/components/Event/CrForm/witnesses'
import { AttendeeRole } from '@/constants'
import { useModals } from '@/hooks/useModals'
import { ReporterFeedbackPayload } from '@/queries/events'
import { Event, EventFeedback, Reporter } from '@/types'

// CR feedback form: the staged sibling of the plain feedback view.
// Lifts one useFeedbackForm instance so the steps and FeedbackFormView share it. Rendered
// via FeedbackForm, so it's the single implementation used wherever the SA feedback form
// appears — ongoing, waiting, and past.
export const StagedFeedbackForm: React.FC<{
  event: Event
  reporter: Reporter
  feedback: ReporterFeedbackPayload | undefined
  onSubmit: () => void
}> = ({ event, reporter, feedback, onSubmit }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [activeWitnessIndex, setActiveWitnessIndex] = useState(0)
  const [pendingScrollToInvalid, setPendingScrollToInvalid] = useState(false)
  const { openConfirmModal } = useModals()

  // Witness count seeds per-witness step state before the form builds: the saved draft's
  // length, else one entry per witness on the event (min 1).
  const witnessCount =
    feedback?.events_feedbacks?.length ||
    Math.max(
      event.attendees.filter(a => a.role === AttendeeRole.WITNESS).length,
      1,
    )

  const bundle = useFeedbackForm({
    event,
    reporter,
    events_feedbacks: feedback?.events_feedbacks as EventFeedback[],
    steps: getInitialStepsState(witnessCount, feedback?.steps),
    sa: feedback?.sa,
    staged: true,
    onSubmit,
  })

  const addWitness = () => {
    const witnessId = uuid()
    const nextValues = appendWitnessFeedback({
      values: bundle.form.values,
      event,
      activeWitnessIndex,
      witnessId,
    })
    if (nextValues === bundle.form.values) {
      return
    }

    bundle.form.setValues(nextValues)
    bundle.form.clearErrors()
    showNotification({ message: 'A new witness section was added.' })
    setTimeout(() => {
      containerRef.current
        ?.querySelector<HTMLElement>(`[data-attendee-id="${witnessId}"] input`)
        ?.focus()
    }, 0)
  }

  const removeActiveWitness = () => {
    if (
      !canRemoveWitnessFeedback(bundle.form.values, activeWitnessIndex, event)
    ) {
      return
    }

    openConfirmModal({
      title: 'Remove witness?',
      onConfirm: () => {
        const nextValues = removeWitnessFeedback(
          bundle.form.values,
          activeWitnessIndex,
          event,
        )
        if (nextValues === bundle.form.values) {
          return
        }

        bundle.form.setValues(nextValues)
        bundle.form.clearErrors()
        setActiveWitnessIndex(Math.max(0, activeWitnessIndex - 1))
      },
      children: (
        <Text size="sm">
          Are you sure you want to remove this witness? Their witness section
          will also be removed.
        </Text>
      ),
      labels: { cancel: 'Cancel', confirm: 'Remove witness' },
      confirmProps: { color: 'red' },
    })
  }

  // Late joiners: once Attendance is done, a Zoom participant nobody is mapped to opens
  // the mapping modal (DR parity).
  useCrParticipantsMapping({
    event,
    form: bundle.form,
    witnessIndex: activeWitnessIndex,
  })

  // On a failed submit: jump to the first witness with errors, then open every step
  // holding an invalid field and scroll to it (deferred until the witness switch renders).
  const onSubmitWithErrors = (errors: Record<string, string>) => {
    bundle.onSubmitWithErrors(errors)

    const firstErrorWitness = Object.keys(errors)
      .map(key => key.match(/^events_feedbacks\.(\d+)\./)?.[1])
      .filter((v): v is string => v !== undefined)
      .map(Number)
      .sort((a, b) => a - b)[0]
    if (firstErrorWitness !== undefined) {
      setActiveWitnessIndex(firstErrorWitness)
    }

    setPendingScrollToInvalid(true)
  }

  useEffect(() => {
    if (!pendingScrollToInvalid) {
      return
    }
    openInvalidStepsAndScroll(containerRef.current, (id, expanded) =>
      bundle.form.setFieldValue(`steps.${id}.expanded`, expanded),
    )
    setPendingScrollToInvalid(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingScrollToInvalid, activeWitnessIndex])

  return (
    <div ref={containerRef} className="space-y-5">
      <WitnessSwitcher
        form={bundle.form}
        activeWitnessIndex={activeWitnessIndex}
        onChange={setActiveWitnessIndex}
        canRemoveActiveWitness={canRemoveWitnessFeedback(
          bundle.form.values,
          activeWitnessIndex,
          event,
        )}
        onRemoveActiveWitness={removeActiveWitness}
      />
      <Stack spacing={0}>
        <PreparationStep event={event} form={bundle.form} />
        <AttendanceStep
          event={event}
          reporter={reporter}
          form={bundle.form}
          witnessIndex={activeWitnessIndex}
          onAddWitness={addWitness}
          addWitnessDisabled={
            !canAddWitnessFeedback(bundle.form.values, activeWitnessIndex)
          }
        />
        <OpeningStep
          event={event}
          reporter={reporter}
          form={bundle.form}
          witnessIndex={activeWitnessIndex}
        />
        <QuestioningStep
          event={event}
          form={bundle.form}
          witnessIndex={activeWitnessIndex}
        />
        <ClosingStep
          event={event}
          form={bundle.form}
          witnessIndex={activeWitnessIndex}
          isLast
        />
      </Stack>
      <ContinueToNextWitnessButton
        form={bundle.form}
        activeWitnessIndex={activeWitnessIndex}
        onContinue={index => {
          setActiveWitnessIndex(index)
          containerRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }}
      />
      <FeedbackFormView
        event={event}
        {...bundle}
        onSubmitWithErrors={onSubmitWithErrors}
        staged
      />
    </div>
  )
}
